// Lenses decide what colour a hospital dot carries. Filters (visibility) are a
// separate concept; a lens never hides a hospital, it only greys the ones with
// no measure. Every lens also yields the letter drawn inside the bubble.
import { gradeColor, getHospitalGrade } from "@/lib/grades";
import type { AtlasHospital } from "./data";

export type LensId =
  | "grade" | "stars" | "recommend" | "google" | "ew" | "mortality" | "readmission"
  | "margin" | "charity" | "ceo" | "value" | "vintage";
export type RGBA = [number, number, number, number];

export const NONE: RGBA = [76, 88, 112, 190];          // slate: no measure for this lens
const DIM: RGBA = [62, 74, 98, 150];

export function hex(h: string, a = 235): RGBA {
  const v = h.replace("#", "");
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16), a];
}

// One five-step "good → bad" ramp shared by every graded lens so the letters
// A–F mean the same thing whatever is being coloured.
export const GRADE_RGBA: Record<string, RGBA> = {
  A: hex(gradeColor("A")), B: hex(gradeColor("B")), C: hex(gradeColor("C")), D: hex(gradeColor("D")), F: hex(gradeColor("F")),
};
export type Letter = "A" | "B" | "C" | "D" | "F";
const LETTERS: Letter[] = ["A", "B", "C", "D", "F"];
/** Map a 0..1 "badness" into A..F. */
function band(t: number): Letter { return LETTERS[Math.min(4, Math.max(0, Math.floor(t * 5)))]; }

export function erBand(m: number): 0 | 1 | 2 | 3 {
  return m <= 120 ? 0 : m <= 180 ? 1 : m <= 240 ? 2 : 3;
}
const VALUE_HIT: RGBA = hex("#ffd166", 245);
const V2026: RGBA = hex("#5fb2ff", 240);
const V2025: RGBA = hex("#7d8fb3", 200);

export interface LegendItem { color: RGBA; label: string }
export interface Lens {
  id: LensId;
  group: "price" | "quality" | "people" | "money" | "file";
  label: string;
  short: string;
  /** Letter for the bubble; null = no measure (bubble shows "–"). */
  letter: (h: AtlasHospital) => Letter | null;
  color: (h: AtlasHospital) => RGBA;
  /** The value as text for the table / tooltip. */
  value: (h: AtlasHospital) => string | null;
  legend: LegendItem[];
  note: string;       // one factual line under the legend
  /** Hex grid: the numeric metric per hospital, how cells aggregate it, the
   *  letter for an aggregated value, and the legend line for the grid. */
  metric: (h: AtlasHospital) => number | null;
  agg: "median" | "mean" | "share";
  bandOf: (v: number) => Letter | null;
  hexNote: string;
  /** Compact value printed beside the bubble on the map ("34 min", "3.2%").
   *  Omitted for the price lens (the letter is the value). */
  badge?: (h: AtlasHospital) => string | null;
}

const gradeLegend = (labels: [string, string, string, string, string], none: string): LegendItem[] => [
  ...LETTERS.map((l, i) => ({ color: GRADE_RGBA[l], label: labels[i] })), { color: NONE, label: none },
];
const byLetter = (fn: (h: AtlasHospital) => Letter | null) => (h: AtlasHospital): RGBA => { const l = fn(h); return l ? GRADE_RGBA[l] : NONE; };

const pct = (v: number) => `${v.toFixed(1)}%`;
const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
export function moneyShort(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(n % 1e9 === 0 ? 0 : 1)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 1)}M`;
  if (a >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return usd0.format(n);
}

// Band rules per lens, stated on the VALUE so the dots and the hex grid share
// one threshold set. The legend prints the thresholds so a letter is never a
// mystery.
export const hospitalGradeOf = (mk: number): Letter => getHospitalGrade(mk);
const starsBand = (v: number): Letter => (["F", "D", "C", "B", "A"] as Letter[])[Math.min(4, Math.max(0, Math.round(v) - 1))];
const recommendBand = (v: number): Letter => (v >= 80 ? "A" : v >= 72 ? "B" : v >= 64 ? "C" : v >= 56 ? "D" : "F");
const googleBand = (v: number): Letter => (v >= 4.3 ? "A" : v >= 3.8 ? "B" : v >= 3.3 ? "C" : v >= 2.8 ? "D" : "F");
const erBandL = (v: number): Letter => (v <= 100 ? "A" : v <= 140 ? "B" : v <= 180 ? "C" : v <= 240 ? "D" : "F");
const mortBand = (v: number): Letter => (v <= 3.0 ? "A" : v <= 3.6 ? "B" : v <= 4.2 ? "C" : v <= 5.0 ? "D" : "F");
const readmBand = (v: number): Letter => (v <= 13.5 ? "A" : v <= 14.5 ? "B" : v <= 15.5 ? "C" : v <= 16.5 ? "D" : "F");
const marginBand = (v: number): Letter => (v >= 8 ? "A" : v >= 3 ? "B" : v >= 0 ? "C" : v >= -5 ? "D" : "F");
const charityBand = (v: number): Letter => (v >= 3 ? "A" : v >= 1.5 ? "B" : v >= 0.7 ? "C" : v >= 0.2 ? "D" : "F");
const ceoBand = (v: number): Letter => (v < 500_000 ? "A" : v < 1_000_000 ? "B" : v < 2_000_000 ? "C" : v < 4_000_000 ? "D" : "F");
const via = (metric: (h: AtlasHospital) => number | null, band: (v: number) => Letter) => (h: AtlasHospital): Letter | null => { const v = metric(h); return v == null ? null : band(v); };

const starsLetter = via((h) => h.cs ?? null, starsBand);
const recommendLetter = via((h) => h.rp ?? null, recommendBand);
const googleLetter = via((h) => h.gr ?? null, googleBand);
const erLetter = via((h) => h.ew ?? null, erBandL);
const mortLetter = via((h) => h.mo ?? null, mortBand);
const readmLetter = via((h) => h.ra ?? null, readmBand);
const marginLetter = via((h) => h.om ?? null, marginBand);
/** Charity care as a share of net patient revenue. */
export function charityShare(h: AtlasHospital): number | null {
  if (h.cc == null || !h.nr || h.nr <= 0) return null;
  return (h.cc / h.nr) * 100;
}
const charityLetter = via(charityShare, charityBand);
/** A $0 top-pay line is an empty filing, not free leadership. */
const ceoPay = (h: AtlasHospital): number | null => (h.ceo != null && h.ceo > 0 ? h.ceo : null);
const ceoLetter = via(ceoPay, ceoBand);

export const LENSES: Record<LensId, Lens> = {
  grade: {
    id: "grade", group: "price", label: "Price grade vs the national median", short: "Price grade",
    letter: (h) => h.g ?? null,
    color: byLetter((h) => h.g ?? null),
    value: (h) => (h.mk == null ? null : `${h.mk > 0 ? "+" : ""}${Math.round(h.mk)}% vs median`),
    legend: gradeLegend(["A ≤ −45%", "B ≤ +20%", "C ≤ +90%", "D ≤ +175%", "F above"], "Ungraded"),
    note: "Each hospital's median markup over the national median, from its own published price file.",
    metric: (h) => h.mk ?? null, agg: "median", bandOf: hospitalGradeOf,
    hexNote: "Each area: the median price markup of the hospitals inside it, graded on the hospital scale.",
  },
  stars: {
    id: "stars", group: "quality", label: "CMS Quality Stars", short: "CMS Quality Stars",
    letter: starsLetter, color: byLetter(starsLetter),
    value: (h) => (h.cs ? `${h.cs} of 5 stars` : null),
    legend: gradeLegend(["A = 5★", "B = 4★", "C = 3★", "D = 2★", "F = 1★"], "Unrated by CMS"),
    note: "CMS Care Compare overall rating, Medicare's own composite. Critical access, psychiatric and children's hospitals are often unrated.",
    metric: (h) => h.cs ?? null, agg: "mean", bandOf: starsBand,
    hexNote: "Each area: the average CMS Quality Star rating of the rated hospitals inside it.",
    badge: (h) => (h.cs ? `${h.cs}/5` : null),
  },
  recommend: {
    id: "recommend", group: "quality", label: "Patients who'd recommend (HCAHPS)", short: "Patients who'd recommend",
    letter: recommendLetter, color: byLetter(recommendLetter),
    value: (h) => (h.rp == null ? null : `${h.rp}% of patients`),
    legend: gradeLegend(["A ≥ 80%", "B ≥ 72%", "C ≥ 64%", "D ≥ 56%", "F below"], "No survey"),
    note: "Share of that hospital's own surveyed patients who said they would definitely recommend it (CMS HCAHPS). Not our recommendation.",
    metric: (h) => h.rp ?? null, agg: "median", bandOf: recommendBand,
    hexNote: "Each area: the median share of surveyed patients who'd recommend the hospitals inside it.",
    badge: (h) => (h.rp == null ? null : `${Math.round(h.rp)}%`),
  },
  google: {
    id: "google", group: "people", label: "Google rating", short: "Google rating",
    letter: googleLetter, color: byLetter(googleLetter),
    value: (h) => (h.gr == null ? null : `${h.gr.toFixed(1)} ★${h.gn ? ` · ${h.gn.toLocaleString()} reviews` : ""}`),
    legend: gradeLegend(["A ≥ 4.3", "B ≥ 3.8", "C ≥ 3.3", "D ≥ 2.8", "F below"], "No rating"),
    note: "Google Maps rating and review count from our snapshot. Reviews are the public's; the letter is just the band.",
    metric: (h) => h.gr ?? null, agg: "mean", bandOf: googleBand,
    hexNote: "Each area: the average Google rating of the hospitals inside it.",
    badge: (h) => (h.gr == null ? null : `${h.gr.toFixed(1)}★`),
  },
  ew: {
    id: "ew", group: "quality", label: "Median ER time", short: "ER time",
    letter: erLetter, color: byLetter(erLetter),
    value: (h) => (h.ew == null ? null : `${h.ew} min`),
    legend: gradeLegend(["A ≤ 100 min", "B ≤ 140", "C ≤ 180", "D ≤ 240", "F longer"], "No ER measure"),
    note: "CMS OP-18b: median minutes from arrival to leaving the emergency department in the reporting period. Not a live wait.",
    metric: (h) => h.ew ?? null, agg: "median", bandOf: erBandL,
    hexNote: "Each area: the median ER time of the hospitals inside it that report one.",
    badge: (h) => (h.ew == null ? null : `${h.ew} min`),
  },
  mortality: {
    id: "mortality", group: "quality", label: "Hospital-wide mortality", short: "Mortality",
    letter: mortLetter, color: byLetter(mortLetter),
    value: (h) => (h.mo == null ? null : `${h.mo.toFixed(1)}% 30-day`),
    legend: gradeLegend(["A ≤ 3.0%", "B ≤ 3.6%", "C ≤ 4.2%", "D ≤ 5.0%", "F higher"], "Not reported"),
    note: "CMS risk-adjusted hospital-wide 30-day mortality rate (Hybrid_HWM). Lower is better.",
    metric: (h) => h.mo ?? null, agg: "median", bandOf: mortBand,
    hexNote: "Each area: the median hospital-wide mortality rate of the hospitals inside it.",
    badge: (h) => (h.mo == null ? null : `${h.mo.toFixed(1)}%`),
  },
  readmission: {
    id: "readmission", group: "quality", label: "Hospital-wide readmission", short: "Readmission",
    letter: readmLetter, color: byLetter(readmLetter),
    value: (h) => (h.ra == null ? null : `${h.ra.toFixed(1)}% 30-day`),
    legend: gradeLegend(["A ≤ 13.5%", "B ≤ 14.5%", "C ≤ 15.5%", "D ≤ 16.5%", "F higher"], "Not reported"),
    note: "CMS risk-adjusted hospital-wide 30-day readmission rate (Hybrid_HWR). Lower is better.",
    metric: (h) => h.ra ?? null, agg: "median", bandOf: readmBand,
    hexNote: "Each area: the median hospital-wide readmission rate of the hospitals inside it.",
    badge: (h) => (h.ra == null ? null : `${h.ra.toFixed(1)}%`),
  },
  margin: {
    id: "margin", group: "money", label: "Operating margin", short: "Operating margin",
    letter: marginLetter, color: byLetter(marginLetter),
    value: (h) => (h.om == null ? null : `${h.om > 0 ? "+" : ""}${pct(h.om)}${h.fy ? ` (FY${h.fy})` : ""}`),
    legend: gradeLegend(["A ≥ +8%", "B ≥ +3%", "C ≥ 0%", "D ≥ −5%", "F deeper loss"], "No cost report"),
    note: "Operating margin from the hospital's CMS cost report (HCRIS), latest fiscal year on file.",
    metric: (h) => h.om ?? null, agg: "median", bandOf: marginBand,
    hexNote: "Each area: the median operating margin of the hospitals inside it with a cost report.",
    badge: (h) => (h.om == null ? null : `${h.om > 0 ? "+" : ""}${h.om.toFixed(1)}%`),
  },
  charity: {
    id: "charity", group: "money", label: "Charity care as a share of revenue", short: "Charity care",
    letter: charityLetter, color: byLetter(charityLetter),
    value: (h) => { const s = charityShare(h); return s == null ? (h.cc != null ? moneyShort(h.cc) : null) : `${s.toFixed(1)}% of revenue · ${moneyShort(h.cc!)}`; },
    legend: gradeLegend(["A ≥ 3%", "B ≥ 1.5%", "C ≥ 0.7%", "D ≥ 0.2%", "F less"], "Not reported"),
    note: "Charity care divided by net patient revenue, both from the CMS cost report. Higher means more free care relative to size.",
    metric: charityShare, agg: "median", bandOf: charityBand,
    hexNote: "Each area: the median charity-care share of revenue of the hospitals inside it.",
    badge: (h) => { const s = charityShare(h); return s == null ? null : `${s.toFixed(1)}%`; },
  },
  ceo: {
    id: "ceo", group: "money", label: "Top executive pay", short: "Executive pay",
    letter: ceoLetter, color: byLetter(ceoLetter),
    value: (h) => { const v = ceoPay(h); return v == null ? null : moneyShort(v); },
    legend: gradeLegend(["A < $0.5M", "B < $1M", "C < $2M", "D < $4M", "F higher"], "No filing"),
    note: "Highest total compensation on the IRS Form 990 (Schedule J). Non-profits only; for-profits don't file one.",
    metric: ceoPay, agg: "median", bandOf: ceoBand,
    hexNote: "Each area: the median top-executive pay of the non-profits inside it that file a 990.",
    badge: (h) => { const v = ceoPay(h); return v == null ? null : moneyShort(v); },
  },
  value: {
    id: "value", group: "price", label: "Value: low price + high rating", short: "Value",
    letter: (h) => ((h.g === "A" || h.g === "B") && (h.cs ?? 0) >= 4 ? "A" : null),
    color: (h) => ((h.g === "A" || h.g === "B") && (h.cs ?? 0) >= 4 ? VALUE_HIT : DIM),
    value: (h) => ((h.g === "A" || h.g === "B") && (h.cs ?? 0) >= 4 ? "Low price, 4–5★" : null),
    legend: [{ color: VALUE_HIT, label: "Grade A–B and 4–5★" }, { color: DIM, label: "Everything else" }],
    note: "Both signals together: a price grade of A or B and a CMS rating of 4 or 5 stars.",
    metric: (h) => ((h.g === "A" || h.g === "B") && (h.cs ?? 0) >= 4 ? 1 : 0), agg: "share", bandOf: (v) => (v >= 0.5 ? "A" : v >= 0.25 ? "B" : v >= 0.1 ? "C" : v > 0 ? "D" : "F"),
    hexNote: "Each area: the share of its hospitals that are grade A–B and rated 4–5 stars (A ≥ half, B ≥ a quarter, C ≥ a tenth, D any, F none).",
  },
  vintage: {
    id: "vintage", group: "file", label: "Price file vintage", short: "Vintage",
    letter: (h) => (h.vy === 2026 ? "A" : h.vy === 2025 ? "B" : null),
    color: (h) => (h.vy === 2026 ? V2026 : h.vy === 2025 ? V2025 : NONE),
    value: (h) => (h.vy ? `${h.vy} filing` : null),
    legend: [{ color: V2026, label: "2026 file" }, { color: V2025, label: "2025 file" }, { color: NONE, label: "Older or unknown" }],
    note: "Which year's price file each hospital's numbers come from. Hospitals republish on their own schedule.",
    metric: (h) => (h.vy === 2026 ? 1 : h.vy === 2025 ? 0 : null), agg: "share", bandOf: (v) => (v >= 0.75 ? "A" : v >= 0.5 ? "B" : v >= 0.25 ? "C" : v > 0 ? "D" : "F"),
    hexNote: "Each area: the share of its hospitals already on a 2026 price file (A ≥ 3 in 4, B ≥ half, C ≥ a quarter, D some, F none).",
    badge: (h) => (h.vy ? String(h.vy) : null),
  },
};

export const LENS_GROUPS: { id: Lens["group"]; label: string; lenses: LensId[] }[] = [
  { id: "price", label: "Price", lenses: ["grade", "value"] },
  { id: "quality", label: "Quality", lenses: ["stars", "recommend", "ew", "mortality", "readmission"] },
  { id: "people", label: "People", lenses: ["google"] },
  { id: "money", label: "Money", lenses: ["margin", "charity", "ceo"] },
  { id: "file", label: "File", lenses: ["vintage"] },
];
export const LENS_ORDER: LensId[] = LENS_GROUPS.flatMap((g) => g.lenses);

export function isLensId(v: string | null | undefined): v is LensId {
  return !!v && (LENS_ORDER as string[]).includes(v);
}

export function rgbaCss(c: RGBA): string {
  return `rgba(${c[0]},${c[1]},${c[2]},${(c[3] / 255).toFixed(2)})`;
}

/** Hospital-level letter for the overall price grade from a median markup. */
export { getHospitalGrade };
